/*
 * disabled the react/jsx-props-no-spreading eslint rule in this file per the
 * documents on HOCs (Higher Order Components):
 * https://github.com/yannickcr/eslint-plugin-react/blob/master/docs/rules/jsx-props-no-spreading.md
 */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Card, FlexBox, Li, Ul, H4, H3, Box } from '@vp/swan';
import { ModTarget } from '@vp/mod-father';
import useSiteData from '../../../hooks/useSiteData';
import { ItemGroupPricingSummary } from './ItemGroupPricingSummary';
import { getItemGroupTitle } from '../../../util/itemGroupDisplayLookup';
import HistoryItemGroupUxModWrapper from '../../../uxMods/orderHistory/HistoryItemGroupUxModWrapper';
import DetailsItemGroupUxModWrapper from '../../../uxMods/orderDetails/DetailsItemGroupUxModWrapper';
import { SourcePageContext } from '../../contexts/SourcePageContext';
import {
  ORDER_CONFIRMATION_SOURCE_PAGE,
  ORDER_DETAILS_SOURCE_PAGE,
  ORDER_HISTORY_SOURCE_PAGE,
} from '../../../constants/orderdetailsui';
import { DesignFeedbackBanner } from '../../orderdetails/designFeedback/DesignFeedbackBanner';
import { getFirstLineItemInFeedbackGroup } from './LineItemGroupUtils';
import { LineItemDataProvider } from '../../contexts/LineItemDataProvider';
import { ItemGroup } from '../../../types/orderDetails';
import { useCustomerFeedbackGroupForItemGroup } from '../../contexts/CustomerFeedbackGroupsProvider';
import LineItemDetails from '../../../types/lineItemDetails';

import { FeedbackType } from '../../../types/feedback/Feedback';
import { FeedbackGroupState } from '../../../types/feedback/FeedbackGroup';

const LineItemGroupUxModWrapper = ({
  sourcePage,
  groupId,
  order,
  children,
}) => {
  const isOrderDetailsOrConfirmationPage =
    sourcePage === ORDER_DETAILS_SOURCE_PAGE ||
    sourcePage === ORDER_CONFIRMATION_SOURCE_PAGE;

  if (sourcePage === ORDER_HISTORY_SOURCE_PAGE) {
    return (
      <HistoryItemGroupUxModWrapper groupId={groupId} order={order}>
        {children}
      </HistoryItemGroupUxModWrapper>
    );
  }

  if (isOrderDetailsOrConfirmationPage) {
    return (
      <DetailsItemGroupUxModWrapper groupId={groupId} order={order}>
        {children}
      </DetailsItemGroupUxModWrapper>
    );
  }

  // TODO: trackError with new Error Tracking API.
  return null;
};

LineItemGroupUxModWrapper.propTypes = {
  sourcePage: PropTypes.string,
  groupId: PropTypes.string,
  order: PropTypes.object,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

/**
 * Wraps the React Component with shared Grouping UI.
 * @param {React.Component} WrappedComponent - the component you want to wrap in the shared grouping UI (i.e. orderhistory/LineItem)
 */
const LineItemGroup = WrappedComponent => {
  /**
   * The component you are wrapping
   * @param {array} items - array of order items
   * @param {string} groupId - groupId for the items
   * @param {object} other - any other props you want to send through to the wrapped component
   */

  const EnhancedComponent = ({ items = [], groupId, ...other }) => {
    const { sourcePage } = useContext(SourcePageContext);
    const { order } = other;
    const { orderNumber, lineItems, itemGroups } = order;
    const groupData = itemGroups
      ? itemGroups.find(group => group.groupId === groupId) || new ItemGroup()
      : new ItemGroup();

    // Get the feedback group associated with one of the items in the item group.
    // It is assumed that if a feedback group exists, this feedback group is associated with all the items in the item group.
    const {
      customerFeedbackGroup: pendingDesignFeedbackGroup,
      customerFeedbackGroupExists: pendingDesignFeedbackGroupExists,
    } = useCustomerFeedbackGroupForItemGroup({
      itemGroup: groupData,
      requestType: FeedbackType.DOCUMENT_REVIEW,
      groupState: FeedbackGroupState.AWAITING_FEEDBACK,
    });

    // Display group level DesignFeedbackBanner based on the first item in the feedback group
    const firstLineItem = pendingDesignFeedbackGroupExists
      ? getFirstLineItemInFeedbackGroup(lineItems, pendingDesignFeedbackGroup)
      : new LineItemDetails();

    const { locale } = useSiteData();
    const isOrderDetailsOrConfirmationPage =
      sourcePage === ORDER_DETAILS_SOURCE_PAGE ||
      sourcePage === ORDER_CONFIRMATION_SOURCE_PAGE;

    // computed object property name and destructuring
    let modData = {};
    ({ [sourcePage]: modData } =
      groupData?.customOrderData?.modData?.host || {});

    const suppressDefaultGroupDisplay = modData?.suppressDefaultGroupDisplay;
    const itemGroupTitle = getItemGroupTitle(groupData, locale);
    return (
      <LineItemGroupUxModWrapper
        sourcePage={sourcePage}
        groupId={groupId}
        order={order}
      >
        <Box aria-labelledby={`group-title-${groupId}`} mb={7}>
          <Card bordered>
            {itemGroupTitle && (
              <FlexBox
                justifyContent="flex-start"
                flexWrap="wrap"
                pr={5}
                mb={3}
              >
                {isOrderDetailsOrConfirmationPage ? (
                  <H3
                    fontSize="standard"
                    fontWeight="bold"
                    data-testid={`group-title-${groupId}`}
                    id={`group-title-${groupId}`}
                  >
                    {itemGroupTitle}
                  </H3>
                ) : (
                  <H4
                    fontSize="standard"
                    fontWeight="bold"
                    data-testid={`group-title-${groupId}`}
                    id={`group-title-${groupId}`}
                  >
                    {itemGroupTitle}
                  </H4>
                )}
              </FlexBox>
            )}
            <FlexBox justifyContent="center" flexWrap="wrap" p={5} mb={3}>
              <LineItemDataProvider
                item={firstLineItem}
                orderNumber={orderNumber}
              >
                <DesignFeedbackBanner />
              </LineItemDataProvider>
            </FlexBox>

            {suppressDefaultGroupDisplay ? (
              <ModTarget name="entireItemGroup" />
            ) : (
              <Ul skin="minimal">
                {items.map(item => (
                  <Li key={`item-${item.id}`} data-testid={`item-${item.id}`}>
                    <WrappedComponent item={item} {...other} />
                  </Li>
                ))}
              </Ul>
            )}
            {/* Only show itemGroup pricing on Order Details because we do not show item-level pricing on Order History
            and the groupTotal requires pricing component data only available on the Order Details UI routes */}
            {isOrderDetailsOrConfirmationPage && (
              <ItemGroupPricingSummary groupId={groupId} order={order} />
            )}
          </Card>
        </Box>
      </LineItemGroupUxModWrapper>
    );
  };

  EnhancedComponent.propTypes = {
    items: PropTypes.array,
    groupId: PropTypes.string,
  };

  return EnhancedComponent;
};

export default LineItemGroup;
